import { inject, Injectable } from '@angular/core';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { AuthService } from '../../auth/auth.service';
import { SvcSignalRHub } from './svc-signal-r-hub.enum';
import { SvcSignalRConnectionConfig, SvcSignalRConnectionInstance, SvcSignalRConnectionStatus } from './svc-signal-r-connection.class';

@Injectable({
  providedIn: 'root',
})
export class SvcSignalRService {
  #appConfig = inject(AppEnvironmentConfig);
  #authService = inject(AuthService);
  #instances: { [key: number]: SvcSignalRConnectionInstance } = {};
  public isWhoChanged: boolean = false;
  public hasStarted: boolean = false;

  constructor() {
    this.#authService.signOut$.subscribe(() => {
      this.disconnectAll();
    });
  }

  public connectTo(hub: SvcSignalRHub, config?: SvcSignalRConnectionConfig): SvcSignalRConnectionInstance {
    const instance = this.#instances[hub] ?? new SvcSignalRConnectionInstance(hub, config);
    this.#instances[hub] = instance;

    if (instance.isDisconnected) {
      this.tryReconnect(instance, { force: true });
    }
    if (instance.isInConnectingMoment || instance.isConnected) {
      return instance;
    }

    const connection = new HubConnectionBuilder()
      .withUrl(`${instance.info.apiURL(this.#appConfig.APIs)}${instance.info.hubPath(this.#authService.userId)}`, {
        accessTokenFactory: () => this.#authService.accessToken,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Error)
      .build();
    instance.setConnection(connection);

    connection.onclose(() => {
      this.tryReconnect(instance);
    });

    for (const methodName of instance.info.listenerMethodNames()) {
      connection.on(methodName, (response) => {
        for (const fn of instance.onCallbacks) {
          fn?.(methodName, response);
        }
      });
    }

    this.connectToHub(instance).catch((error) => console.warn(`[SignalR][${instance.info.name()}] start failed`, error));

    return instance;
  }

  public getAliveInstanceFromHub(hub: SvcSignalRHub): SvcSignalRConnectionInstance {
    return this.#instances[hub];
  }

  private isInstanceAvailable(instance: SvcSignalRConnectionInstance): boolean {
    return Object.keys(this.#instances).some((hub) => this.#instances[hub] === instance);
  }

  private canInstanceReconnect(instance: SvcSignalRConnectionInstance): boolean {
    return instance.canRetryToConnect && this.isInstanceAvailable(instance);
  }

  private async tryReconnect(instance: SvcSignalRConnectionInstance, options?: { force?: boolean }): Promise<void> {
    const force = options?.force ?? false;
    if (instance.isInConnectingMoment) return;
    if (!force && !this.canInstanceReconnect(instance)) {
      instance.status = SvcSignalRConnectionStatus.DISCONNECTED;
      return;
    }
    if (force) instance.timeoutNextReconnection = 5000;
    try {
      if (instance.connection) {
        instance.status = SvcSignalRConnectionStatus.IN_RECONNECTING;
        await this.connectToHub(instance);
      }
    } catch (e) {
      if ((e?.message?.toString() ?? '').includes(`is not in the 'Disconnected'`)) {
        instance.status = SvcSignalRConnectionStatus.CONNECTED;
        instance.timeoutNextReconnection = Math.max(instance.timeoutNextReconnection - 5000, 5000);
        return;
      }
      console.warn(`[SignalR][${instance.info.name()}] reconnect failed, retrying in ${instance.timeoutNextReconnection}ms`, e);
      setTimeout(() => {
        instance.status = SvcSignalRConnectionStatus.DISCONNECTED;
        this.tryReconnect(instance);
      }, instance.timeoutNextReconnection);
      instance.timeoutNextReconnection += 5000;
    }
  }

  private async connectToHub(instance: SvcSignalRConnectionInstance): Promise<void> {
    if (instance.isNone) instance.status = SvcSignalRConnectionStatus.IN_CONNECTING;
    await instance.connection.start();
    instance.status = SvcSignalRConnectionStatus.CONNECTED;
  }

  public async disconnect(hub: SvcSignalRHub): Promise<void> {
    const instance = this.#instances[hub];
    delete this.#instances[hub];
    if (instance) {
      await instance.connection?.stop();
      instance.status = SvcSignalRConnectionStatus.DISCONNECTED;
    }
  }

  public disconnectAll(): void {
    const instances = this.#instances;
    this.#instances = {};
    for (const hub in instances) {
      if (!instances[hub]) continue;
      instances[hub].canRetryToConnect = false;
      instances[hub].connection?.stop();
    }
  }
}

